import React, { useEffect, useState } from 'react'
import { Button, Input, Modal, Space, message } from 'antd'
import { CloudDownloadOutlined, LinkOutlined, SaveOutlined } from '@ant-design/icons'
import { CgvDataJson, CgvDataJsonForm, parseCgvDataJson } from './cgvDataJsonForm'
import { facturationCgvService, SiretLookupResult } from './facturationCgvService'

const COMPANY_FIELDS: (keyof CgvDataJson)[] = [
  'raison_sociale',
  'forme_juridique',
  'capital_social',
  'address',
  'postal_code',
  'city',
  'name_rcs',
  'siren',
  'siret',
]

function hasCompanyData(data: CgvDataJson) {
  return COMPANY_FIELDS.some(k => {
    const v = data[k]
    return typeof v === 'string' && v.trim().length > 0
  })
}

export function copyCgvSignatureLink(cgvId: string) {
  const url = `https://cloud.prosperences.fr/cn/a/cgv/${cgvId}`
  navigator.clipboard.writeText(url)
  message.success('Lien de signature copié dans le presse-papier')
}

export interface CgvEditModalProps {
  open: boolean
  onClose: () => void
  onSave: (data: CgvDataJson) => void | Promise<void>
  dataJson?: string | CgvDataJson | null
  /** Id CGV pour le lien de signature (désactivé si absent) */
  cgvId?: string | null
  initialSiret?: string | null
  saving?: boolean
  isEdit?: boolean
}

/** Modal CGV unique (Org + Facturation) : lookup SIRET, formulaire, copier le lien. */
export function CgvEditModal(props: CgvEditModalProps) {
  const [draft, setDraft] = useState<CgvDataJson>({})
  const [lookupSiret, setLookupSiret] = useState('')
  const [lookingUp, setLookingUp] = useState(false)

  useEffect(() => {
    if (!props.open) return
    setDraft(parseCgvDataJson(props.dataJson))
    const fromData = parseCgvDataJson(props.dataJson).siret
    setLookupSiret((props.initialSiret || fromData || '').trim())
  }, [props.open, props.dataJson, props.initialSiret])

  const applyCompanyFields = (fromApi: SiretLookupResult, overwrite: boolean) => {
    setDraft(prev => {
      const next = { ...prev }
      const mapping: Partial<CgvDataJson> = {
        raison_sociale: fromApi.raison_sociale || '',
        forme_juridique: fromApi.forme_juridique || '',
        capital_social: fromApi.capital_social || prev.capital_social || '',
        address: fromApi.address || '',
        postal_code: fromApi.postal_code || '',
        city: fromApi.city || '',
        name_rcs: fromApi.name_rcs || '',
        siren: fromApi.siren || '',
        siret: fromApi.siret || lookupSiret,
      }
      for (const key of COMPANY_FIELDS) {
        const incoming = mapping[key]
        if (incoming === undefined) continue
        const current = prev[key]
        if (overwrite || !current || String(current).trim() === '') {
          next[key] = incoming
        }
      }
      return next
    })
  }

  const onLookupSiret = async () => {
    const siret = lookupSiret.trim()
    if (!/^\d{14}$/.test(siret)) {
      message.error('Le SIRET doit contenir exactement 14 chiffres')
      return
    }
    setLookingUp(true)
    try {
      const { data } = await facturationCgvService.lookupSiret(siret)
      if (!data.ok) {
        message.error(data.error || 'SIRET introuvable')
        return
      }
      if (hasCompanyData(draft)) {
        Modal.confirm({
          title: 'Écraser les données société ?',
          content:
            'Des champs société sont déjà renseignés. Voulez-vous les remplacer par les données API ?',
          okText: 'Écraser',
          cancelText: 'Garder / compléter les vides',
          onOk: () => applyCompanyFields(data, true),
          onCancel: () => applyCompanyFields(data, false),
        })
      } else {
        applyCompanyFields(data, true)
        message.success('Données société chargées')
      }
    } catch (e) {
      console.error(e)
      message.error('Erreur lors du lookup SIRET')
    } finally {
      setLookingUp(false)
    }
  }

  return (
    <Modal
      title={
        <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{props.isEdit ? 'Modifier les infos CGV' : 'Ajouter les CGV'}</span>
          {/* <Button
            icon={<LinkOutlined />}
            disabled={!props.cgvId}
            onClick={() => props.cgvId && copyCgvSignatureLink(props.cgvId)}
          >
            Copier le lien de signature
          </Button> */}
        </Space>
      }
      open={props.open}
      onCancel={props.onClose}
      width={640}
      destroyOnClose
      footer={[
        <Button
          icon={<LinkOutlined />}
          disabled={!props.cgvId}
          onClick={() => props.cgvId && copyCgvSignatureLink(props.cgvId)}
        >
          Copier le lien de signature
        </Button>,
        <Button key="cancel" onClick={props.onClose}>
          Annuler
        </Button>,
        <Button
          key="save"
          type="primary"
          icon={<SaveOutlined />}
          loading={props.saving}
          onClick={() => props.onSave(draft)}
        >
          Enregistrer
        </Button>,
      ]}
    >
      <Space style={{ marginBottom: 16, width: '100%' }} wrap>
        <Input
          placeholder="SIRET (14 chiffres)"
          value={lookupSiret}
          maxLength={14}
          onChange={e => setLookupSiret(e.target.value)}
          style={{ width: 180 }}
        />
        <Button icon={<CloudDownloadOutlined />} loading={lookingUp} onClick={onLookupSiret}>
          Charger les données via l&apos;INPI / l&apos;AE
        </Button>
      </Space>
      <CgvDataJsonForm layout="grid" value={draft} onChange={setDraft} />
    </Modal>
  )
}
